import React, { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';

import { useAppConfig } from '@state';
import {
  Button,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  Input,
  Label,
  useUserAuthentication,
} from '@ohif/ui-next';

import { getSupabaseClient } from '../../utils/supabaseClient';
import { STUDY_LIST_PATH } from '../../constants/routes';

function SignInPage() {
  const navigate = useNavigate();
  const [appConfig] = useAppConfig();
  const [{ user }] = useUserAuthentication();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  const afterAuthPath = useMemo(
    () => (appConfig.showStudyList ? STUDY_LIST_PATH : '/'),
    [appConfig.showStudyList]
  );

  useEffect(() => {
    if (user) {
      navigate(afterAuthPath, { replace: true });
    }
  }, [user, navigate, afterAuthPath]);

  const onSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    const client = getSupabaseClient();
    if (!client) {
      setError('Supabase is not configured.');
      return;
    }
    setSubmitting(true);
    const { error: signError } = await client.auth.signInWithPassword({ email, password });
    setSubmitting(false);
    if (signError) {
      setError(signError.message);
      return;
    }
    navigate(afterAuthPath, { replace: true });
  };

  return (
    <div className="bg-background text-foreground flex min-h-full w-full items-center justify-center p-6">
      <Card className="w-full max-w-md">
        <CardHeader>
          <CardTitle>Sign in</CardTitle>
        </CardHeader>
        <CardContent>
          <form
            className="flex flex-col gap-4"
            onSubmit={onSubmit}
          >
            <div className="flex flex-col gap-2">
              <Label htmlFor="signin-email">Email</Label>
              <Input
                id="signin-email"
                name="email"
                type="email"
                autoComplete="email"
                value={email}
                onChange={ev => setEmail(ev.target.value)}
                required
              />
            </div>
            <div className="flex flex-col gap-2">
              <Label htmlFor="signin-password">Password</Label>
              <Input
                id="signin-password"
                name="password"
                type="password"
                autoComplete="current-password"
                value={password}
                onChange={ev => setPassword(ev.target.value)}
                required
              />
            </div>
            {error ? <p className="text-destructive text-sm">{error}</p> : null}
            <Button
              type="submit"
              disabled={submitting}
              name="signin"
            >
              {submitting ? 'Signing in…' : 'Sign in'}
            </Button>
            <p className="text-muted-foreground text-sm">
              No account?{' '}
              <Link
                className="text-highlight underline"
                to="/sign-up"
              >
                Create an account
              </Link>
            </p>
          </form>
        </CardContent>
      </Card>
    </div>
  );
}

export default SignInPage;
